/**
 * ConfirmEmailScreen — handles the deep-link / web route that arrives
 * after the user clicks the confirmation link in their inbox.
 *
 * Route: (auth)/confirm-email/[token]
 *
 * States:
 *   loading  — POST in flight; spinner
 *   success  — account confirmed; "Sign in" CTA
 *   error    — token invalid/expired; resend form + back link
 */

import React, { useEffect, useState } from 'react'
import { View, Text, ActivityIndicator, Platform, Linking } from 'react-native'
import { Link, useLocalSearchParams, useRouter } from 'expo-router'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../../components/ui/Toast'
import { confirmEmail, resendConfirmation } from '../../../lib/api'
import { reportError } from '../../../lib/reporting'

// Custom scheme (app.json `scheme`). Used to bounce a user who confirmed in a
// mobile browser back into the installed native app.
const APP_DEEP_LINK = 'warcarp://login'

type ScreenState = 'loading' | 'success' | 'error'

export default function ConfirmEmailScreen() {
  const { token } = useLocalSearchParams<{ token: string }>()
  const router = useRouter()
  const { showToast } = useToast()

  const [state, setState] = useState<ScreenState>('loading')
  const [resendEmail, setResendEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      setState('error')
      return
    }

    let cancelled = false
    confirmEmail(token)
      .then(() => { if (!cancelled) setState('success') })
      .catch(() => { if (!cancelled) setState('error') })

    return () => { cancelled = true }
  }, [token])

  async function handleResend() {
    const trimmed = resendEmail.trim()
    if (!trimmed) return
    setResendLoading(true)
    try {
      await resendConfirmation(trimmed)
      showToast('Confirmation email sent', 'success')
    } catch {
      showToast('Could not resend — please try again', 'error')
    } finally {
      setResendLoading(false)
    }
  }

  if (state === 'loading') {
    return (
      <View className="flex-1 bg-canvas justify-center items-center gap-4">
        <ActivityIndicator size="large" />
        <Text className="font-typed text-sm text-ink-soft">Confirming your email…</Text>
      </View>
    )
  }

  if (state === 'success') {
    // On the web confirm page (the email link usually opens a browser), offer
    // to jump back into the native app via the custom scheme. On the native
    // confirm screen we're already in the app, so just go to Sign In.
    const isWeb = Platform.OS === 'web'
    const openApp = () => {
      Linking.openURL(APP_DEEP_LINK).catch((err) =>
        // If the app isn't installed the scheme won't resolve; report it (no
        // silent catch) and fall back to web sign-in.
        reportError(err, { tags: { area: 'confirm-email-open-app' } }),
      )
    }
    return (
      <View className="flex-1 bg-canvas justify-center px-6 gap-8">
        <View className="gap-3">
          <Text className="text-4xl font-stencil text-ink uppercase">Account confirmed!</Text>
          <Text className="font-typed text-sm text-ink-soft">
            Your account is now active. You can sign in and join the battle.
          </Text>
        </View>
        {isWeb ? (
          <View className="gap-5">
            <Button
              title="Sign In"
              size="lg"
              onPress={() => router.replace('/(auth)/login')}
            />
            <Text className="font-typed text-xs text-ink-soft text-center">
              Came from the app?{' '}
              <Text className="text-blood font-semibold" onPress={openApp}>
                Open it
              </Text>
            </Text>
          </View>
        ) : (
          <Button
            title="Sign In"
            size="lg"
            onPress={() => router.replace('/(auth)/login')}
          />
        )}
      </View>
    )
  }

  // error state
  return (
    <KeyboardAwareScrollView
      className="flex-1 bg-canvas"
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, gap: 32 }}
    >
      <View className="gap-3">
        <Text className="text-2xl font-stencil text-ink uppercase">Link not valid</Text>
        <Text className="font-typed text-sm text-ink-soft">
          We couldn't confirm this link. It may have expired or already been used.
        </Text>
      </View>

      <View className="gap-4">
        <Text className="font-typed text-sm text-ink">
          Enter your email to receive a new confirmation link:
        </Text>
        <Input
          label="Email"
          value={resendEmail}
          onChangeText={setResendEmail}
          keyboardType="email-address"
          autoComplete="email"
          autoCapitalize="none"
        />
        <Button
          title="Resend confirmation email"
          onPress={handleResend}
          loading={resendLoading}
          disabled={!resendEmail.trim()}
        />
      </View>

      <Link href="/(auth)/login" className="text-blood font-semibold text-base text-center font-typed">
        Back to Sign In
      </Link>
    </KeyboardAwareScrollView>
  )
}
